import React, { useContext, useEffect, useState } from 'react';
import CommonLayout from '../../components/CommonLayout';
import { UserContext } from '../../context/UserContext';
import API from '../../config/axiosConfig';
import AppUsageSection from './AppUsageSection';
import { PERIODS, type Period } from './periods';
import './Stats.scss';

/**
 * Super-admin only, enforced both here and by superAdminCheck on every
 * /api/analytics query endpoint.
 *
 * Shows exactly two things: how many sessions there were, and app usage.
 */
const Stats: React.FC = () => {
  const { user } = useContext(UserContext);
  const isSuperAdmin = Boolean(user && user.isSuperAdmin);

  const [period, setPeriod] = useState<Period>(PERIODS[0]);
  const [sessions, setSessions] = useState<number | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await API.get<{ sessions: number }>(
          `/api/analytics/sessions?from=${encodeURIComponent(period.from().toISOString())}`,
          { handlingErrorLocally: true }
        );
        if (!cancelled) setSessions(res.data?.sessions ?? null);
      } catch {
        // Observational data only — show a dash, never an error.
        if (!cancelled) setSessions(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, period]);

  if (!user) {
    return (
      <CommonLayout>
        <div className="stats-page" dir="rtl">
          <div className="stats-container">
            <h1>סטטיסטיקות</h1>
            <p>נא להתחבר.</p>
          </div>
        </div>
      </CommonLayout>
    );
  }

  if (!isSuperAdmin) {
    return (
      <CommonLayout>
        <div className="stats-page" dir="rtl">
          <div className="stats-container">
            <h1>סטטיסטיקות</h1>
            <p>אין הרשאה לצפייה בעמוד זה.</p>
          </div>
        </div>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <div className="stats-page" dir="rtl">
        <div className="stats-container">
          <h1 className="stats-title">סטטיסטיקות</h1>

          <label className="stats-control">
            <span>טווח</span>
            <select
              value={period.id}
              onChange={(e) => setPeriod(PERIODS.find((p) => p.id === e.target.value) || PERIODS[0])}
            >
              {PERIODS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <div className="stats-kpi-row">
            <div className="stats-kpi-card">
              <div className="stats-kpi-label">כניסות למערכת (sessions)</div>
              <div className="stats-kpi-value">{sessions ?? '—'}</div>
            </div>
          </div>

          <AppUsageSection period={period} />
        </div>
      </div>
    </CommonLayout>
  );
};

export default Stats;
